import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  CreditCard, Plus, Search, ExternalLink, Edit2, Trash2,
  User, Building2, Mail, ChevronLeft, ChevronRight
} from "lucide-react";

interface Card {
  uid: number;
  personal_id: string;
  name_en: string | null;
  name_zh_cht: string | null;
  name_zh_chs: string | null;
  title_en: string | null;
  company_en: string | null;
  email: string | null;
  status: "active" | "inactive" | "draft";
  owner_email: string | null;
  owner_name: string | null;
  created_at: string;
}

const STATUS_STYLES: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  inactive: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  draft: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
};

const LIMIT = 20;

export default function MyCardsPage() {
  const { user, actingAs } = useAuth();
  const [cards, setCards] = useState<Card[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const searchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const isSecretary = user?.role === "secretary";
  const effectiveName = isSecretary ? (actingAs?.name || actingAs?.email) : (user?.name || user?.email);
  const totalPages = Math.ceil(total / LIMIT);

  const loadCards = useCallback(async (p: number, q: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: String(LIMIT) });
      if (q) params.set("search", q);
      const data = await api.get<{ cards: Card[]; total: number }>(`/api/v1/cards?${params}`);
      setCards(data.cards);
      setTotal(data.total);
    } catch (err: any) {
      toast.error(err.message || "Failed to load cards");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSecretary && !actingAs) return; // Don't load if no user selected
    loadCards(page, search);
  }, [page, search, actingAs?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = (val: string) => {
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(val);
      setPage(1);
    }, 350);
  };

  const handleDelete = async (uid: number, name: string | null) => {
    if (!confirm(`Delete "${name || "this card"}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/v1/cards/${uid}`);
      setCards(prev => prev.filter(c => c.uid !== uid));
      setTotal(t => t - 1);
      toast.success("Card deleted");
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  // Secretary: no user selected
  if (isSecretary && !actingAs) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-center px-4">
        <div className="w-16 h-16 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
          <User className="w-8 h-8 text-amber-600 dark:text-amber-400" />
        </div>
        <div>
          <h2 className="text-xl font-semibold">Select a User</h2>
          <p className="text-muted-foreground text-sm mt-2 max-w-sm">
            Use the user switcher in the sidebar to select which user you want to manage cards for.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">
            {isSecretary ? `${effectiveName}'s Cards` : "My Cards"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {total > 0
              ? `${total} card${total !== 1 ? "s" : ""}${isSecretary ? " · managing on behalf" : ""}`
              : isSecretary ? "Managing cards on behalf of this user" : "Your digital business cards"}
          </p>
        </div>
        <Link
          to="/dashboard/cards/new"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 transition-colors text-sm font-medium shadow-sm"
        >
          <Plus className="w-4 h-4" />
          New Card
        </Link>
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          placeholder="Search by name, ID, or email..."
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 border border-input rounded-xl bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
      </div>

      {/* Content */}
      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="h-44 bg-muted rounded-xl animate-pulse" />
          ))}
        </div>
      ) : cards.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
          <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center">
            <CreditCard className="w-8 h-8 text-muted-foreground opacity-50" />
          </div>
          <div>
            <p className="font-semibold text-foreground">
              {search ? "No cards match your search" : "No cards yet"}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {search ? "Try a different query" : "Create your first digital business card"}
            </p>
          </div>
          {!search && (
            <Link
              to="/dashboard/cards/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create First Card
            </Link>
          )}
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {cards.map((card) => {
              const displayName = card.name_en || card.name_zh_cht || card.name_zh_chs || "Unnamed";
              return (
                <div
                  key={card.uid}
                  className="group bg-card border border-border rounded-xl p-4 hover:shadow-md hover:border-primary/30 transition-all"
                >
                  {/* Card header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0 pr-2">
                      <h3 className="font-semibold text-foreground truncate">{displayName}</h3>
                      {card.title_en && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{card.title_en}</p>
                      )}
                    </div>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase shrink-0 ${STATUS_STYLES[card.status] || STATUS_STYLES.inactive}`}>
                      {card.status}
                    </span>
                  </div>

                  {/* Card details */}
                  <div className="space-y-1.5 mb-4">
                    {card.company_en && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Building2 className="w-3 h-3 shrink-0" />
                        <span className="truncate">{card.company_en}</span>
                      </div>
                    )}
                    {card.email && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Mail className="w-3 h-3 shrink-0" />
                        <span className="truncate">{card.email}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-mono">
                      <CreditCard className="w-3 h-3 shrink-0" />
                      <span className="truncate">{card.personal_id}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-3 border-t border-border">
                    <a
                      href={`/u/${card.personal_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-medium transition-colors"
                    >
                      <ExternalLink className="w-3 h-3" />
                      View
                    </a>
                    <Link
                      to={`/dashboard/cards/${card.uid}`}
                      className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Edit2 className="w-3 h-3" />
                      Edit
                    </Link>
                    <button
                      onClick={() => handleDelete(card.uid, card.name_en)}
                      className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-destructive transition-colors ml-auto"
                    >
                      <Trash2 className="w-3 h-3" />
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
              <p className="text-sm text-muted-foreground">
                {((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, total)} of {total}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 rounded-lg border border-input hover:bg-accent disabled:opacity-40 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm font-medium px-1">{page} / {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 rounded-lg border border-input hover:bg-accent disabled:opacity-40 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
